import { WeekDay } from '@angular/common';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import * as moment from 'moment';

import { CensusSnapshotService } from './census-snapshot.service';
import { ConfigService } from './config.service';
import { Constants } from '../utilities/constants.util';
import { CostCenter } from 'src/shared/models/cost-center.model';
import { CostCenterService } from './cost-center.service';
import { InpatientDetails } from '../models/inpatient-details.model';
import { PatientCensusRecord } from '../models/patient-census-record.model';
import { ShiftBlock } from '../models/shift-block.model';
import { ShiftBlockService } from './shift-block.service';
import { ShiftRecord } from 'src/shared/models/shift-record.model';
import { ShiftRecordServiceBase } from './shift-record-base.service';

@Injectable({
    providedIn: 'root'
})
export class InpatientShiftRecordService extends ShiftRecordServiceBase {

  constructor(
    protected _configService: ConfigService,
    protected _http: HttpClient,
    protected _costCenterService: CostCenterService,
    protected _shiftBlockService: ShiftBlockService,
    private _censusSnapshotService: CensusSnapshotService,) {	
        super(_configService, _http, _costCenterService, _shiftBlockService);
  }
   
    protected getPreviousShiftBlockPromise(costCenter: CostCenter, shiftBlock: ShiftBlock, workflowTime: string) {
        const previousTime = this.getInpatientPreviousTime(workflowTime);
        const crossesWeekendBoundary = this.crossesWeekendBoundary(shiftBlock, previousTime);
        const firstActiveBlock = this.firstActiveBlock(shiftBlock, previousTime);
        
        if (!shiftBlock.previousShiftBlockId || crossesWeekendBoundary || firstActiveBlock) {
            return this._shiftBlockService.loadShiftBlock(costCenter.id, previousTime.format(Constants.FacilityTimeFormat));
        } else {
            return this._shiftBlockService.loadShiftBlockById(shiftBlock.previousShiftBlockId);
        }
    }

    protected async prepareShiftRecordDetails(
        costCenter: CostCenter, 
        shiftBlock: ShiftBlock, 
        shiftRecord: ShiftRecord, 
        workflowTime: string): Promise<ShiftRecord> {
        let preparedShiftRecord = this.prepareShiftRecord(costCenter, shiftBlock, shiftRecord, workflowTime);

        preparedShiftRecord.patientCensusRecord = new PatientCensusRecord();
        preparedShiftRecord.projectedCensusChurn = null;
        preparedShiftRecord.actualCensusChurn = null;
        preparedShiftRecord.patientRatioRecords = costCenter.inpatientConfiguration.totalPatientCensus ? null : [];

        let details = await this.getInpatientDetails(preparedShiftRecord);

        preparedShiftRecord.measureRecords = details.measures;
        preparedShiftRecord.staffingPlans = details.staffingPlans;
        preparedShiftRecord.varianceReasonRecords = details.varianceReasons;

        if (shiftBlock.id) {
            preparedShiftRecord.actualCensusChurn = details.censusSnapshots.find(census => !census.isProjected);        
            preparedShiftRecord.patientCensusRecord = details.patientCensus ? details.patientCensus : new PatientCensusRecord();
            preparedShiftRecord.projectedCensusChurn = details.censusSnapshots.find(census => census.isProjected);
    
            if (!costCenter.inpatientConfiguration.totalPatientCensus) {
                preparedShiftRecord.patientRatioRecords = details.patientRatios;
            }
        }

        return preparedShiftRecord;
    }

    private async getInpatientDetails(shiftRecord: ShiftRecord): Promise<InpatientDetails> {                
        return await Promise.all([           
            this._censusSnapshotService.getInpatientShiftRecordDetails(shiftRecord)
        ]).then(values => {
            let details = new InpatientDetails();
            if (values != null && values[0] != null) {
                details.measures = values[0].measures;
                details.staffingPlans = values[0].staffingPlans;
                details.varianceReasons = values[0].varianceReasons;
                details.censusSnapshots = values[0].censusSnapshots;          
                details.patientCensus = values[0].patientCensus;
                details.patientRatios = values[0].patientRatios;
            }

            return details;
        });
    }

    private getInpatientPreviousTime(workflowTime: string) {
        const previousTime = moment(workflowTime).subtract(4, 'hour');
        return previousTime;
    }

    private firstActiveBlock(shiftBlock: ShiftBlock, previousTime: moment.Moment) {
        return moment(shiftBlock.created).isAfter(previousTime);
    }

    private crossesWeekendBoundary(shiftBlock: ShiftBlock, previousTime: moment.Moment) {
        if (shiftBlock.isWeekday && shiftBlock.isWeekend) {
            return false;
        }
        let crossesWeekendBoundary = shiftBlock.isWeekday && (previousTime.weekday() === WeekDay.Saturday || previousTime.weekday() === WeekDay.Sunday)
                                    || shiftBlock.isWeekend && (previousTime.weekday() !== WeekDay.Saturday && previousTime.weekday() !== WeekDay.Sunday);
        return crossesWeekendBoundary;
    }

}
